// MATCHING FUND FEATURE — Public website tracker (Bootstrap).
// Fetches the NestJS public summary and renders full or compact view.
// Auto-hides when pledged === 0 or on fetch error.
import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://api.caprover.sokaab.com/api";

const STATUS_COLORS = {
  not_started: "#9E9E9E",
  partial: "#F39C12",
  fulfilled: "#28A745",
  overpaid: "#1565C0",
};

const formatMoney = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;

const MatchingFundTracker = ({ projectId, compact = false, summary: prefetchedSummary }) => {
  const [summary, setSummary] = useState(prefetchedSummary || null);
  const [loading, setLoading] = useState(!prefetchedSummary);

  useEffect(() => {
    // If a prefetched summary was provided (batch fetch on the parent), use it and skip the network call.
    if (prefetchedSummary) {
      setSummary(prefetchedSummary);
      setLoading(false);
      return;
    }
    let cancelled = false;
    if (!projectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API_BASE}/projects/${projectId}/matching-fund`)
      .then((res) => {
        if (!cancelled) setSummary(res.data);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, prefetchedSummary]);

  if (loading || !summary) return null;
  // MATCHING FUND FEATURE — restricted to projects created on/after June 1, 2026 (see API eligible flag).
  if (summary.eligible === false) return null;
  if (!summary.pledged || summary.pledged <= 0) return null;

  const percent = Math.min(Math.max(summary.percent_paid || 0, 0), 100);
  const status = summary.status || "not_started";
  const barColor = STATUS_COLORS[status];

  if (compact) {
    return (
      <div style={{ marginTop: "8px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "12px",
            color: "#555",
            marginBottom: "4px",
          }}
        >
          <span>
            <strong>Matching Fund:</strong> {formatMoney(summary.disbursed)} /{" "}
            {formatMoney(summary.pledged)}
          </span>
          <span style={{ color: barColor, fontWeight: 600 }}>
            {Math.round(percent)}%
          </span>
        </div>
        <div className="progress" style={{ height: "4px" }}>
          <div
            className="progress-bar"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin="0"
            aria-valuemax="100"
            style={{
              width: `${percent}%`,
              background: barColor,
            }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: "10px",
        marginBottom: "10px",
      }}
    >
      <div className="progress" style={{ height: "6px", marginBottom: "8px" }}>
        <div
          className="progress-bar"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin="0"
          aria-valuemax="100"
          style={{
            width: `${percent}%`,
            background: barColor,
          }}
        ></div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "13px",
          marginBottom: "10px",
        }}
      >
        <span style={{ fontWeight: 600, color: "#010038" }}>
          {formatMoney(summary.disbursed)} disbursed
        </span>
        <span style={{ color: "#777" }}>
          of {formatMoney(summary.pledged)} pledged
        </span>
      </div>
    </div>
  );
};

export default MatchingFundTracker;
